import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Mail, 
  Calendar, 
  Clock, 
  MapPin, 
  Users,
  CheckCircle2,
  XCircle,
  AlertCircle,
  BookOpen,
  Heart,
  Plus,
  Timer,
  ClipboardList,
  Paperclip,
  Download,
  FileText,
  File,
  FileSpreadsheet,
  FileType
} from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab04b277-779a-46f6-a15b-41b90e7d7254/artifacts/ccdpbqku_TBP-New%20Logo%20Design%20conv%201.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VolunteerPortal() {
  const [email, setEmail] = useState('');
  const [volunteer, setVolunteer] = useState(null);
  const [myEvents, setMyEvents] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [myHours, setMyHours] = useState({ hours: [], total_hours: 0, approved_hours: 0, pending_hours: 0 });
  const [myTasks, setMyTasks] = useState([]);
  const [myDocuments, setMyDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [showHourDialog, setShowHourDialog] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);
  const [hourForm, setHourForm] = useState({
    hours: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    role: '',
    event_id: ''
  });

  // Check for auto-login from URL params (admin preview mode)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const volunteerIdParam = params.get('vid');
    const autoParam = params.get('auto');
    
    if (volunteerIdParam && autoParam === 'true') {
      setIsAdminView(true);
      // Auto-lookup using admin preview endpoint
      adminPreviewVolunteer(volunteerIdParam);
    }
  }, []);

  const adminPreviewVolunteer = async (volunteerId) => {
    setLoading(true);
    setLookupError('');
    
    try {
      const response = await fetch(`${API}/admin/volunteer-portal/${volunteerId}/preview`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setVolunteer(data);
        setEmail(data.email);
        await fetchMyEvents(data.volunteer_id);
        await fetchMyHours(data.volunteer_id);
        await fetchMyTasks(data.volunteer_id);
        await fetchMyDocuments(data.volunteer_id);
      } else {
        const error = await response.json();
        setLookupError(error.detail || 'Volunteer not found');
      }
    } catch (error) {
      setLookupError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const VOLUNTEER_ROLES = [
    "Volunteer Coordinator",
    "Family Engagement Support",
    "Bookmobile Navigator",
    "Assembler",
    "Bookshelf Artisan",
    "Event Support",
    "Fundraising Support",
    "Administrative Support",
    "Social Media & Marketing",
    "Book Curator",
    "Photographer",
    "Other"
  ];

  const lookupVolunteer = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLookupError('');
    
    try {
      const response = await fetch(`${API}/volunteer-portal/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      
      if (response.ok) {
        const data = await response.json();
        setVolunteer(data);
        // Fetch events and hours
        await fetchMyEvents(data.volunteer_id);
        await fetchAvailableEvents();
        await fetchMyHours(data.volunteer_id);
        await fetchMyTasks(data.volunteer_id);
        await fetchMyDocuments(data.volunteer_id);
        toast.success(`Welcome back, ${data.first_name}!`);
      } else {
        const error = await response.json();
        setLookupError(error.detail || 'Volunteer not found');
        setVolunteer(null);
      }
    } catch (error) {
      setLookupError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyEvents = async (volunteerId) => {
    try {
      const response = await fetch(`${API}/volunteer-portal/${volunteerId}/events`);
      if (response.ok) {
        setMyEvents(await response.json());
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchAvailableEvents = async () => {
    try {
      const response = await fetch(`${API}/volunteer-portal/available-events`);
      if (response.ok) {
        setAvailableEvents(await response.json());
      }
    } catch (error) {
      console.error('Error fetching available events:', error);
    }
  };

  const fetchMyHours = async (volunteerId) => {
    try {
      const response = await fetch(`${API}/volunteer-portal/${volunteerId}/hours`);
      if (response.ok) {
        setMyHours(await response.json());
      }
    } catch (error) {
      console.error('Error fetching hours:', error);
    }
  };

  const fetchMyTasks = async (volunteerId) => {
    try {
      const response = await fetch(`${API}/volunteer-portal/${volunteerId}/tasks`);
      if (response.ok) {
        setMyTasks(await response.json());
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchMyDocuments = async (volunteerId) => {
    try {
      const response = await fetch(`${API}/volunteer-portal/${volunteerId}/documents`);
      if (response.ok) {
        setMyDocuments(await response.json());
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const downloadUrl = `${API}/volunteer-portal/${volunteer.volunteer_id}/documents/${doc.document_id}/download`;
      console.log('Downloading from:', downloadUrl);
      
      const response = await fetch(downloadUrl);
      
      if (response.ok) {
        const data = await response.json();
        const link = document.createElement('a');
        link.href = `data:${data.file_type};base64,${data.file_data}`;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Document downloaded');
      } else {
        const error = await response.json().catch(() => ({ detail: 'Download failed' }));
        console.error('Download failed:', error);
        toast.error(error.detail || 'Failed to download document');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document. Please try again.');
    }
  };

  const getDocumentIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (fileType?.includes('spreadsheet') || fileType?.includes('excel') || fileType?.includes('csv')) 
      return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    if (fileType?.includes('word') || fileType?.includes('document')) 
      return <FileType className="w-5 h-5 text-blue-600" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      const response = await fetch(`${API}/volunteer-portal/${volunteer.volunteer_id}/tasks/${taskId}/status?status=${status}`, {
        method: 'PUT'
      });
      if (response.ok) {
        toast.success('Task status updated');
        fetchMyTasks(volunteer.volunteer_id);
      } else {
        toast.error('Failed to update task status');
      }
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const downloadAttachment = (attachment) => {
    // Create a link and trigger download
    const link = document.createElement('a');
    link.href = attachment.file_data;
    link.download = attachment.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const submitHours = async (e) => {
    e.preventDefault();
    
    if (!volunteer || volunteer.status !== 'Approved') {
      toast.error('Only approved volunteers can log hours');
      return;
    }
    
    try {
      const response = await fetch(`${API}/volunteer-portal/hours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          volunteer_id: volunteer.volunteer_id,
          hours: parseFloat(hourForm.hours),
          date: hourForm.date,
          description: hourForm.description,
          role: hourForm.role,
          event_id: hourForm.event_id || null
        })
      });
      
      if (response.ok) {
        toast.success('Hours submitted for review!');
        setShowHourDialog(false);
        setHourForm({
          hours: '',
          date: new Date().toISOString().split('T')[0],
          description: '',
          role: '',
          event_id: ''
        });
        await fetchMyHours(volunteer.volunteer_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit hours');
      }
    } catch (error) {
      toast.error('Failed to submit hours');
    }
  };

  const registerForEvent = async (eventId) => {
    if (!volunteer || volunteer.status !== 'Approved') {
      toast.error('Only approved volunteers can register for events');
      return;
    }
    
    try {
      const response = await fetch(`${API}/events/${eventId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ volunteer_id: volunteer.volunteer_id })
      });
      
      if (response.ok) {
        toast.success('Successfully registered for event!');
        await fetchMyEvents(volunteer.volunteer_id);
        await fetchAvailableEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to register');
      }
    } catch (error) {
      toast.error('Failed to register for event');
    }
  };

  const unregisterFromEvent = async (eventId) => {
    try {
      const response = await fetch(`${API}/events/${eventId}/unregister`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ volunteer_id: volunteer.volunteer_id })
      });
      
      if (response.ok) {
        toast.success('Successfully unregistered from event');
        await fetchMyEvents(volunteer.volunteer_id);
        await fetchAvailableEvents();
      } else {
        toast.error('Failed to unregister');
      }
    } catch (error) {
      toast.error('Failed to unregister from event');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Approved': 'bg-emerald-100 text-emerald-800',
      'Pending': 'bg-amber-100 text-amber-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    const icons = {
      'Approved': <CheckCircle2 className="w-3 h-3 mr-1" />,
      'Pending': <AlertCircle className="w-3 h-3 mr-1" />,
      'Rejected': <XCircle className="w-3 h-3 mr-1" />
    };
    return (
      <Badge className={`${styles[status] || 'bg-slate-100'} flex items-center`}>
        {icons[status]}
        {status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const isRegistered = (eventId) => {
    return myEvents.some(event => event.event_id === eventId);
  };

  const logout = () => {
    setVolunteer(null);
    setMyEvents([]);
    setAvailableEvents([]);
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="volunteer-portal">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Link to="/">
                <img src={LOGO_URL} alt="The Bookshelf Project" className="h-12 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-4">
              {volunteer && (
                <Button variant="ghost" onClick={logout} className="text-slate-600">
                  Sign Out
                </Button>
              )}
              <Link to="/">
                <Button variant="ghost" className="text-slate-600 hover:text-[#1a3a5c]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      {/* Admin View Banner */}
      {isAdminView && volunteer && (
        <div className="bg-amber-500 text-white py-2 px-4 text-center">
          <span className="font-medium">Admin Preview Mode</span>
          <span className="mx-2">—</span>
          <span>Viewing portal as {volunteer.first_name} {volunteer.last_name}</span>
          <button 
            onClick={() => window.close()}
            className="ml-4 underline hover:no-underline"
          >
            Close Preview
          </button>
        </div>
      )}

      <section className="py-12 bg-gradient-to-br from-[#1a3a5c] to-[#2a5a8c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Volunteer Portal
          </h1>
          <p className="text-lg text-white/80">
            View events, manage your schedule, and track your impact
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!volunteer ? (
          /* Login Form */
          <Card className="max-w-md mx-auto border-0 shadow-lg" data-testid="volunteer-lookup-form">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#5cc8e8]" />
              </div>
              <CardTitle className="text-[#1a3a5c]">Access Your Portal</CardTitle>
              <CardDescription>
                Enter the email you used when applying to volunteer
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={lookupVolunteer} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="lookup-email-input"
                  />
                </div>
                {lookupError && (
                  <p className="text-red-500 text-sm flex items-center gap-1" data-testid="lookup-error">
                    <XCircle className="w-4 h-4" />
                    {lookupError}
                  </p>
                )}
                <Button 
                  type="submit" 
                  className="w-full bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                  disabled={loading}
                  data-testid="lookup-submit-btn"
                >
                  {loading ? 'Looking up...' : 'Access Portal'}
                </Button>
              </form>
              <div className="mt-6 text-center text-sm text-slate-500">
                <p>Don't have an account?</p>
                <Link to="/apply" className="text-[#5cc8e8] hover:underline font-medium">
                  Apply to become a volunteer
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Volunteer Dashboard */
          <div className="space-y-6">
            {/* Profile Summary */}
            <Card className="border-0 shadow-lg" data-testid="volunteer-profile-card">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1a3a5c] to-[#5cc8e8] rounded-full flex items-center justify-center text-white text-xl font-bold">
                      {volunteer.first_name[0]}{volunteer.last_name[0]}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-[#1a3a5c]">
                        {volunteer.first_name} {volunteer.last_name}
                      </h2>
                      <p className="text-slate-500">{volunteer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(volunteer.status)}
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Total Hours</p>
                      <p className="text-2xl font-bold text-[#5cc8e8]">{volunteer.total_hours}</p>
                    </div>
                  </div>
                </div>
                
                {volunteer.status === 'Pending' && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Application Pending</p>
                        <p className="text-sm text-amber-700">
                          Your volunteer application is being reviewed. You'll be able to sign up for events once approved.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {volunteer.status === 'Rejected' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Application Not Approved</p>
                        <p className="text-sm text-red-700">
                          Unfortunately, your application was not approved at this time. Please contact us for more information.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Events Tabs */}
            <Tabs defaultValue="available" className="space-y-4">
              <TabsList className="bg-white shadow-sm border w-full justify-start">
                <TabsTrigger value="available" className="flex-1 sm:flex-none">Available Events</TabsTrigger>
                <TabsTrigger value="my-events" className="flex-1 sm:flex-none">
                  My Events {myEvents.length > 0 && `(${myEvents.length})`}
                </TabsTrigger>
                <TabsTrigger value="my-hours" className="flex-1 sm:flex-none">
                  My Hours
                </TabsTrigger>
                <TabsTrigger value="my-tasks" className="flex-1 sm:flex-none" data-testid="tasks-tab">
                  Tasks {myTasks.length > 0 && `(${myTasks.length})`}
                </TabsTrigger>
                <TabsTrigger value="my-documents" className="flex-1 sm:flex-none" data-testid="documents-tab">
                  Documents {myDocuments.length > 0 && `(${myDocuments.length})`}
                </TabsTrigger>
              </TabsList>

              {/* Available Events Tab */}
              <TabsContent value="available" data-testid="available-events-tab">
                <div className="space-y-4">
                  {availableEvents.length > 0 ? (
                    availableEvents.map((event) => (
                      <Card key={event.event_id} className="border-0 shadow-md overflow-hidden" data-testid={`event-card-${event.event_id}`}>
                        <div className="flex flex-col lg:flex-row">
                          {/* Event Date Badge */}
                          <div className="bg-[#1a3a5c] text-white p-4 lg:w-32 flex lg:flex-col items-center justify-center text-center">
                            <Calendar className="w-5 h-5 mb-1 mr-2 lg:mr-0" />
                            <div>
                              <p className="text-sm opacity-80">
                                {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                              </p>
                              <p className="text-2xl font-bold">
                                {new Date(event.date).getDate()}
                              </p>
                            </div>
                          </div>
                          
                          {/* Event Details */}
                          <CardContent className="flex-1 p-4">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-[#1a3a5c]">{event.title}</h3>
                                <p className="text-sm text-slate-500 mb-2">{event.event_type}</p>
                                <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {event.start_time} - {event.end_time}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {event.location}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    {event.spots_available} spots left
                                  </span>
                                </div>
                                {event.description && (
                                  <p className="text-sm text-slate-600 mt-2">{event.description}</p>
                                )}
                              </div>
                              
                              <div className="flex sm:flex-col gap-2">
                                {isRegistered(event.event_id) ? (
                                  <>
                                    <Badge className="bg-emerald-100 text-emerald-800">
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Registered
                                    </Badge>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => unregisterFromEvent(event.event_id)}
                                      data-testid={`unregister-btn-${event.event_id}`}
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                ) : event.is_full ? (
                                  <Badge className="bg-slate-100 text-slate-600">Event Full</Badge>
                                ) : volunteer.status !== 'Approved' ? (
                                  <Button 
                                    disabled 
                                    size="sm" 
                                    className="bg-slate-300 cursor-not-allowed"
                                  >
                                    Approval Required
                                  </Button>
                                ) : (
                                  <Button 
                                    size="sm"
                                    onClick={() => registerForEvent(event.event_id)}
                                    className="bg-[#5cc8e8] hover:bg-[#5cc8e8]/90"
                                    data-testid={`register-btn-${event.event_id}`}
                                  >
                                    Sign Up
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-8 text-center">
                        <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">No upcoming events available</p>
                        <p className="text-sm text-slate-400 mt-1">Check back soon for new volunteer opportunities!</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* My Events Tab */}
              <TabsContent value="my-events" data-testid="my-events-tab">
                <div className="space-y-4">
                  {myEvents.length > 0 ? (
                    myEvents.map((event) => (
                      <Card key={event.event_id} className="border-0 shadow-md overflow-hidden border-l-4 border-l-emerald-500">
                        <CardContent className="p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                <span className="text-xs text-emerald-600 font-medium">REGISTERED</span>
                              </div>
                              <h3 className="text-lg font-semibold text-[#1a3a5c]">{event.title}</h3>
                              <p className="text-sm text-slate-500 mb-2">{event.event_type}</p>
                              <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {formatDate(event.date)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {event.start_time} - {event.end_time}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {event.location}
                                </span>
                              </div>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => unregisterFromEvent(event.event_id)}
                              className="text-red-600 hover:bg-red-50 hover:text-red-700"
                              data-testid={`cancel-event-btn-${event.event_id}`}
                            >
                              Cancel Registration
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-8 text-center">
                        <Heart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">You haven't signed up for any events yet</p>
                        <p className="text-sm text-slate-400 mt-1">Browse available events to start volunteering!</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* My Hours Tab */}
              <TabsContent value="my-hours" data-testid="my-hours-tab">
                <div className="space-y-4">
                  {/* Hours Summary Card */}
                  <Card className="border-0 shadow-md bg-gradient-to-br from-[#1a3a5c] to-[#2a5a8c] text-white">
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-semibold mb-2">Your Volunteer Hours</h3>
                          <div className="flex flex-wrap gap-4">
                            <div>
                              <p className="text-sm text-white/60">Total Hours</p>
                              <p className="text-3xl font-bold">{myHours.total_hours.toFixed(1)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-white/60">Approved</p>
                              <p className="text-2xl font-bold text-emerald-300">{myHours.approved_hours.toFixed(1)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-white/60">Pending Review</p>
                              <p className="text-2xl font-bold text-amber-300">{myHours.pending_hours.toFixed(1)}</p>
                            </div>
                          </div>
                        </div>
                        {volunteer.status === 'Approved' && (
                          <Dialog open={showHourDialog} onOpenChange={setShowHourDialog}>
                            <DialogTrigger asChild>
                              <Button className="bg-white text-[#1a3a5c] hover:bg-white/90" data-testid="log-hours-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Log Hours
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Log Volunteer Hours</DialogTitle>
                              </DialogHeader>
                              <form onSubmit={submitHours} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label htmlFor="hours">Hours *</Label>
                                    <Input
                                      id="hours"
                                      type="number"
                                      step="0.5"
                                      min="0.5"
                                      max="24"
                                      placeholder="e.g., 4"
                                      value={hourForm.hours}
                                      onChange={(e) => setHourForm(prev => ({ ...prev, hours: e.target.value }))}
                                      required
                                      data-testid="hours-input"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="date">Date *</Label>
                                    <Input
                                      id="date"
                                      type="date"
                                      value={hourForm.date}
                                      onChange={(e) => setHourForm(prev => ({ ...prev, date: e.target.value }))}
                                      required
                                      data-testid="date-input"
                                    />
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="role">Role *</Label>
                                  <Select
                                    value={hourForm.role}
                                    onValueChange={(value) => setHourForm(prev => ({ ...prev, role: value }))}
                                  >
                                    <SelectTrigger data-testid="role-select">
                                      <SelectValue placeholder="Select your role" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {VOLUNTEER_ROLES.map((role) => (
                                        <SelectItem key={role} value={role}>{role}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                {myEvents.length > 0 && (
                                  <div className="space-y-2">
                                    <Label htmlFor="event">Related Event (Optional)</Label>
                                    <Select
                                      value={hourForm.event_id}
                                      onValueChange={(value) => setHourForm(prev => ({ ...prev, event_id: value }))}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select event (if applicable)" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="">No specific event</SelectItem>
                                        {myEvents.map((event) => (
                                          <SelectItem key={event.event_id} value={event.event_id}>
                                            {event.title} ({event.date})
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                                <div className="space-y-2">
                                  <Label htmlFor="description">Description *</Label>
                                  <Textarea
                                    id="description"
                                    placeholder="Describe what you did during your volunteer time..."
                                    value={hourForm.description}
                                    onChange={(e) => setHourForm(prev => ({ ...prev, description: e.target.value }))}
                                    required
                                    data-testid="description-input"
                                  />
                                </div>
                                <div className="flex justify-end gap-3 pt-2">
                                  <Button type="button" variant="outline" onClick={() => setShowHourDialog(false)}>
                                    Cancel
                                  </Button>
                                  <Button 
                                    type="submit" 
                                    className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                                    disabled={!hourForm.hours || !hourForm.role || !hourForm.description}
                                    data-testid="submit-hours-btn"
                                  >
                                    Submit Hours
                                  </Button>
                                </div>
                              </form>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Hours List */}
                  {myHours.hours.length > 0 ? (
                    <div className="space-y-3">
                      {myHours.hours.map((hour) => (
                        <Card key={hour.hour_id} className={`border-0 shadow-sm ${hour.approved ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-amber-400'}`}>
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Timer className="w-4 h-4 text-[#5cc8e8]" />
                                  <span className="font-semibold text-[#1a3a5c]">{hour.hours} hours</span>
                                  {hour.approved ? (
                                    <Badge className="bg-emerald-100 text-emerald-800 text-xs">Approved</Badge>
                                  ) : (
                                    <Badge className="bg-amber-100 text-amber-800 text-xs">Pending Review</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">{hour.description}</p>
                                <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                                  <span>{hour.date}</span>
                                  <span>•</span>
                                  <span>{hour.role}</span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-8 text-center">
                        <Timer className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">No hours logged yet</p>
                        <p className="text-sm text-slate-400 mt-1">
                          {volunteer.status === 'Approved' 
                            ? "Click 'Log Hours' to record your volunteer time!"
                            : "You'll be able to log hours once your application is approved."}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* My Tasks Tab */}
              <TabsContent value="my-tasks" data-testid="my-tasks-tab">
                <div className="space-y-4">
                  {myTasks.length > 0 ? (
                    myTasks.map((task) => (
                      <Card key={task.task_id} className="border-0 shadow-md overflow-hidden" data-testid={`task-card-${task.task_id}`}>
                        <CardContent className="p-4">
                          <div className="flex flex-col gap-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <ClipboardList className="w-5 h-5 text-[#1a3a5c]" />
                                  <h3 className="text-lg font-semibold text-[#1a3a5c]">{task.title}</h3>
                                </div>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  <Badge variant="outline" className="text-xs">{task.related_interest}</Badge>
                                  <Badge className={`text-xs ${
                                    task.priority === 'High' ? 'bg-red-100 text-red-800' :
                                    task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>{task.priority}</Badge>
                                  <Badge className={`text-xs ${
                                    task.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                    task.status === 'In Progress' ? 'bg-purple-100 text-purple-800' :
                                    'bg-slate-100 text-slate-800'
                                  }`}>{task.status}</Badge>
                                </div>
                              </div>
                            </div>
                            
                            <div className="p-3 bg-slate-50 rounded-lg">
                              <p className="text-sm text-slate-600">{task.description}</p>
                            </div>
                            
                            {task.due_date && (
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Calendar className="w-4 h-4" />
                                <span>Due: {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                              </div>
                            )}
                            
                            {/* Attachments */}
                            {task.attachments && task.attachments.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {task.attachments.map((att, index) => (
                                  <button
                                    key={index}
                                    onClick={() => downloadAttachment(att)}
                                    className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100"
                                  >
                                    <Paperclip className="w-3 h-3" />
                                    {att.filename}
                                    <Download className="w-3 h-3" />
                                  </button>
                                ))}
                              </div>
                            )}
                            
                            {/* Status Update Buttons */}
                            {volunteer.status === 'Approved' && task.status !== 'Completed' && (
                              <div className="flex gap-2 pt-2 border-t">
                                {task.status !== 'In Progress' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateTaskStatus(task.task_id, 'In Progress')}
                                    className="flex-1"
                                    data-testid={`start-task-${task.task_id}`}
                                  >
                                    <Clock className="w-4 h-4 mr-1" />
                                    Start Task
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  onClick={() => updateTaskStatus(task.task_id, 'Completed')}
                                  className="flex-1 bg-green-600 hover:bg-green-700"
                                  data-testid={`complete-task-${task.task_id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Mark Complete
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-8 text-center">
                        <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">No tasks assigned yet</p>
                        <p className="text-sm text-slate-400 mt-1">
                          When you're assigned tasks, they'll appear here.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* My Documents Tab */}
              <TabsContent value="my-documents" data-testid="my-documents-tab">
                <div className="space-y-4">
                  {myDocuments.length > 0 ? (
                    myDocuments.map((doc) => (
                      <Card key={doc.document_id} className="border-0 shadow-md">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getDocumentIcon(doc.file_type)}
                              <div>
                                <h3 className="font-semibold text-[#1a3a5c]">{doc.title}</h3>
                                <p className="text-sm text-slate-500">
                                  {doc.filename} • {formatFileSize(doc.file_size)}
                                </p>
                                <p className="text-xs text-slate-400">
                                  Shared on {new Date(doc.assigned_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </p>
                              </div>
                            </div>
                            <Button
                              onClick={() => downloadDocument(doc)}
                              className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                              size="sm"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card className="border-0 shadow-md">
                      <CardContent className="py-12 text-center text-slate-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">No Documents Available</p>
                        <p className="text-sm">
                          Documents shared with you by The Bookshelf Project will appear here
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Volunteer Roles */}
            {volunteer.volunteer_roles && volunteer.volunteer_roles.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-[#1a3a5c] flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Your Volunteer Roles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {volunteer.volunteer_roles.map((role, index) => (
                      <Badge key={index} variant="outline" className="px-3 py-1">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#1a3a5c] border-t border-white/10 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              The Bookshelf Project
            </Link>
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} The Bookshelf Project. Building in-home libraries for children.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
